import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    Grid,
    useAppDispatch,
    Box,
    Typography,
    Stack,
    Divider,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Select,
    MenuItem,
    FormControl,
    Alert,
    useAppSelector,
    Switch,
    FormControlLabel,
    IconButton,
    Tooltip,
} from '../../../base';
import { useGoogleLogin } from '@react-oauth/google';
import {
    organizationActions,
    organizationSelectors,
    userActions,
    userSelectors,
} from '../../../actions';
import { useOutlook } from '../../../hooks';
import { Icon } from '../../../base';
import { RoutePaths } from '../../../routes/RoutePaths';
import {
    ICompanyMailbox,
    IMailboxConfiguration,
    IUserMailbox,
    MailProviders,
    EmailSettingsContext,
} from '../../../common';

interface EmailMailbox {
    id: number;
    emailAddress: string;
    name: string;
    provider: MailProviders;
    status?: string;
}

interface Props {
    context?: EmailSettingsContext;
}

const MOCK_DATA = {
    companyMailboxes: [
        {
            id: 1,
            emailAddress: 'john.doe@company.com',
            name: 'John Doe',
            status: 'ACTIVE',
            provider: MailProviders.GMAIL,
            createdBy: {
                id: 1,
                firstName: 'John',
                lastName: 'Doe',
                email: 'john@company.com',
            },
            createdAt: '2024-01-15T10:00:00Z',
        },
        {
            id: 2,
            emailAddress: 'recruitment@company.com',
            name: 'Recruitment Team',
            status: 'ACTIVE',
            provider: MailProviders.OUTLOOK,
            createdBy: {
                id: 2,
                firstName: 'Jane',
                lastName: 'Smith',
                email: 'jane@company.com',
            },
            createdAt: '2024-02-20T14:30:00Z',
        },
        {
            id: 3,
            emailAddress: 'hr@company.com',
            name: 'HR Department',
            status: 'INACTIVE',
            provider: MailProviders.GMAIL,
            createdBy: {
                id: 1,
                firstName: 'John',
                lastName: 'Doe',
                email: 'john@company.com',
            },
            createdAt: '2024-01-10T09:00:00Z',
        },
    ] as ICompanyMailbox[],
    mailboxConfiguration: [
        {
            id: 1,
            mailbox: 1,
            mailboxType: 'SYSTEM_EMAIL',
            mailboxSelection: 'COMPANY',
        },
        {
            id: 2,
            mailbox: 2,
            mailboxType: 'CANDIDATE_COMMUNICATION',
            mailboxSelection: 'COMPANY',
        },
        {
            id: 3,
            mailbox: null,
            mailboxType: 'APPLICATION_SUBMISSION',
            mailboxSelection: 'COMPANY',
        },
    ] as IMailboxConfiguration[],
};

const normalizeUserMailboxes = (mailboxes: IUserMailbox[]): EmailMailbox[] =>
    mailboxes.map((mb) => ({
        id: mb.id,
        emailAddress: mb.emailAddress,
        name: mb.name,
        provider: mb.provider,
    }));

const normalizeCompanyMailboxes = (
    mailboxes: ICompanyMailbox[]
): EmailMailbox[] =>
    mailboxes.map((mb) => ({
        id: mb.id,
        emailAddress: mb.emailAddress,
        name: mb.name,
        provider: mb.provider,
        status: mb.status,
    }));

const EmailSettingsTab = ({
    context = EmailSettingsContext.COMPANY,
}: Props) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const isUserContext = context === EmailSettingsContext.USER;

    const companySettings = useAppSelector(
        organizationSelectors.getCompanySettings
    );
    const userMailboxes = useAppSelector(userSelectors.getUserMailboxes);
    const isUpdateLoading = useAppSelector(
        organizationSelectors.getIsUpdateCompanySettingsLoading
    );

    const [useMockData, setUseMockData] = useState(false);

    const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
        open: boolean;
        mailbox: EmailMailbox | null;
    }>({ open: false, mailbox: null });

    const [localMailboxConfiguration, setLocalMailboxConfiguration] = useState<
        IMailboxConfiguration[]
    >([]);

    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const connectGmail = useGoogleLogin({
        onSuccess: (tokenResponse) => {
            if (isUserContext) {
                dispatch(
                    userActions.postConnectUserGmail({
                        code: tokenResponse.code,
                    })
                );
            } else {
                dispatch(
                    organizationActions.postConnectGmail({
                        code: tokenResponse.code,
                    })
                );
            }
        },
        onError: () => {
            console.log('Login Failed');
        },
        scope: 'openid email profile https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.modify https://www.googleapis.com/auth/calendar',
        flow: 'auth-code',
    });

    const { connectOutlook } = useOutlook(context, { handleRedirect: false });

    const mailboxes: EmailMailbox[] = useMockData
        ? normalizeCompanyMailboxes(MOCK_DATA.companyMailboxes)
        : isUserContext
          ? normalizeUserMailboxes(userMailboxes)
          : normalizeCompanyMailboxes(companySettings?.companyMailboxes || []);

    const mailboxConfiguration = useMemo(
        () =>
            useMockData
                ? MOCK_DATA.mailboxConfiguration
                : isUserContext
                  ? []
                  : companySettings?.mailboxConfiguration || [],
        [useMockData, isUserContext, companySettings?.mailboxConfiguration]
    );

    useEffect(() => {
        setLocalMailboxConfiguration(mailboxConfiguration);
        setHasUnsavedChanges(false);
    }, [mailboxConfiguration]);

    const hasEmails = mailboxes.length > 0;
    const canAddMoreEmails = isUserContext ? mailboxes.length === 0 : true;
    const isLocalDev =
        process.env.NODE_ENV === 'development' &&
        window.location.hostname === 'localhost';

    const handleRemoveMailbox = (mailbox: EmailMailbox) => {
        setDeleteConfirmDialog({ open: true, mailbox });
    };

    const handleConfirmDelete = () => {
        if (deleteConfirmDialog.mailbox) {
            if (isUserContext) {
                dispatch(
                    userActions.deleteUserMailbox({
                        id: deleteConfirmDialog.mailbox.id,
                    })
                );
            } else {
                dispatch(
                    organizationActions.deleteCompanyMailbox({
                        id: deleteConfirmDialog.mailbox.id,
                    })
                );
            }
        }
        setDeleteConfirmDialog({ open: false, mailbox: null });
    };

    const handleCancelDelete = () => {
        setDeleteConfirmDialog({ open: false, mailbox: null });
    };

    const handleReconnect = (mailbox: EmailMailbox) => {
        // TODO: Call reconnect API when available
        console.log('Reconnect mailbox:', mailbox.id);
    };

    const handleCustomizeTemplate = () => {
        navigate(RoutePaths.emailTemplates);
    };

    const handleMailboxConfigurationChange = (
        configId: number,
        mailboxId: string
    ) => {
        const selectedMailboxId = mailboxId === '' ? null : parseInt(mailboxId);
        
        setLocalMailboxConfiguration((prev) =>
            prev.map((config) =>
                config.id === configId
                    ? { ...config, mailbox: selectedMailboxId }
                    : config
            )
        );
        
        setHasUnsavedChanges(true);
    };

    const handleSaveConfiguration = () => {
        const configurationPayload = localMailboxConfiguration.map((config) => ({
            id: config.id,
            mailbox: config.mailbox,
        }));

        dispatch(
            organizationActions.updateCompanySettings({
                mailboxConfiguration: configurationPayload,
            })
        );
    };

    const getMailboxTypeName = (mailboxType: string): string => {
        return (
            t(`settings.emailSettings.mailboxTypes.${mailboxType}.title`) ||
            mailboxType
        );
    };

    const getMailboxTypeDescription = (mailboxType: string): string => {
        return t(
            `settings.emailSettings.mailboxTypes.${mailboxType}.description`
        );
    };

    return (
        <Box sx={{ p: 3 }}>
            <Grid container spacing={4}>
                {/* Mock Data Toggle (Local Dev only, company context only) */}
                {isLocalDev && !isUserContext && (
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                mb: -2,
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={useMockData}
                                        onChange={(e) =>
                                            setUseMockData(e.target.checked)
                                        }
                                    />
                                }
                                label="Use Mock Data (Dev)"
                            />
                        </Box>
                    </Grid>
                )}

                {/* Add Email Section */}
                <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                        {t('settings.emailSettings.addEmail')}
                    </Typography>
                    {isUserContext && !canAddMoreEmails && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {t(
                                'settings.emailSettings.userContextSingleEmailLimit'
                            )}
                        </Alert>
                    )}
                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="outlined"
                            onClick={() => connectGmail()}
                            disabled={!canAddMoreEmails}
                            startIcon={<Icon name="GoogleLogoIcon" size={20} />}
                            sx={{
                                textTransform: 'none',
                                borderColor: '#dadce0',
                                color: '#3c4043',
                                '&:hover': {
                                    borderColor: '#d2e3fc',
                                    backgroundColor: '#f8f9fa',
                                },
                                fontWeight: 500,
                                px: 3,
                                py: 1,
                            }}
                        >
                            {t('settings.emailSettings.addGoogleAccount')}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => connectOutlook()}
                            disabled={!canAddMoreEmails}
                            startIcon={
                                <Icon name="MicrosoftLogoIcon" size={20} />
                            }
                            sx={{
                                textTransform: 'none',
                                borderColor: '#8c8c8c',
                                color: '#5e5e5e',
                                '&:hover': {
                                    borderColor: '#106ebe',
                                    backgroundColor: '#f3f2f1',
                                },
                                fontWeight: 500,
                                px: 3,
                                py: 1,
                            }}
                        >
                            {t('settings.emailSettings.addOutlookAccount')}
                        </Button>
                    </Stack>
                </Grid>

                {/* Only show sections below if there are emails */}
                {hasEmails && (
                    <>
                        <Grid item xs={12}>
                            <Divider />
                        </Grid>

                        {/* Connected Emails Section */}
                        <Grid item xs={12}>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ mb: 2 }}
                            >
                                {t('settings.emailSettings.connectedEmails')}
                            </Typography>

                            {/* Header Row */}
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={5}>
                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                        color="text.secondary"
                                    >
                                        {t(
                                            'settings.emailSettings.emailAccountHeader'
                                        )}
                                    </Typography>
                                </Grid>
                                {!isUserContext && (
                                    <Grid item xs={3}>
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                            color="text.secondary"
                                            textAlign="center"
                                        >
                                            {t(
                                                'settings.emailSettings.statusHeader'
                                            )}
                                        </Typography>
                                    </Grid>
                                )}
                                <Grid item xs={isUserContext ? 7 : 4}>
                                    {/* Empty space for actions column */}
                                </Grid>
                            </Grid>
                            <Divider />

                            {/* Email Account Rows */}
                            {mailboxes.map((mailbox: EmailMailbox, index) => (
                                <Box key={mailbox.id}>
                                    <Grid
                                        container
                                        spacing={2}
                                        alignItems="center"
                                        sx={{ py: 2 }}
                                    >
                                        <Grid item xs={5}>
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                            >
                                                <Icon
                                                    name={
                                                        mailbox.provider ===
                                                        MailProviders.GMAIL
                                                            ? 'GoogleLogoIcon'
                                                            : 'MicrosoftLogoIcon'
                                                    }
                                                    size={24}
                                                />
                                                <Box>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={600}
                                                    >
                                                        {mailbox.emailAddress}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {mailbox.name}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Grid>
                                        {!isUserContext && mailbox.status && (
                                            <Grid
                                                item
                                                xs={3}
                                                display="flex"
                                                justifyContent="center"
                                            >
                                                <Chip
                                                    label={
                                                        mailbox.status ===
                                                        'ACTIVE'
                                                            ? t(
                                                                  'settings.emailSettings.emailStatus.active'
                                                              )
                                                            : t(
                                                                  'settings.emailSettings.emailStatus.inactive'
                                                              )
                                                    }
                                                    sx={{
                                                        bgcolor:
                                                            mailbox.status ===
                                                            'ACTIVE'
                                                                ? '#e8f5e9'
                                                                : '#ffebee',
                                                        color:
                                                            mailbox.status ===
                                                            'ACTIVE'
                                                                ? '#2e7d32'
                                                                : '#c62828',
                                                        fontWeight: 500,
                                                        border: 'none',
                                                    }}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </Grid>
                                        )}
                                        <Grid
                                            item
                                            xs={isUserContext ? 7 : 4}
                                            display="flex"
                                            justifyContent="flex-end"
                                        >
                                            <Stack
                                                direction="row"
                                                spacing={0.5}
                                            >
                                                {!isUserContext &&
                                                    mailbox.status !==
                                                        'ACTIVE' && (
                                                        <Tooltip
                                                            title={t(
                                                                'settings.emailSettings.reconnect'
                                                            )}
                                                        >
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    handleReconnect(
                                                                        mailbox
                                                                    )
                                                                }
                                                                color="primary"
                                                            >
                                                                <Icon
                                                                    name="SyncOutlineIcon"
                                                                    size={20}
                                                                />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                <Tooltip
                                                    title={t(
                                                        'settings.emailSettings.remove'
                                                    )}
                                                >
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            handleRemoveMailbox(
                                                                mailbox
                                                            )
                                                        }
                                                        color="error"
                                                    >
                                                        <Icon
                                                            name="DeleteIcon"
                                                            size={22}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                    {index < mailboxes.length - 1 && (
                                        <Divider />
                                    )}
                                </Box>
                            ))}
                        </Grid>

                        {/* Mailbox Configuration Section (company context only) */}
                        {!isUserContext && localMailboxConfiguration.length > 0 && (
                            <>
                                <Grid item xs={12}>
                                    <Divider />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography
                                        variant="h6"
                                        gutterBottom
                                        sx={{ mb: 1 }}
                                    >
                                        {t(
                                            'settings.emailSettings.mailboxConfiguration'
                                        )}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mb: 3 }}
                                    >
                                        {t(
                                            'settings.emailSettings.mailboxConfigurationDescription'
                                        )}
                                    </Typography>

                                    {/* Header Row */}
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        <Grid item xs={6}>
                                            <Typography
                                                variant="body2"
                                                fontWeight={600}
                                                color="text.secondary"
                                            >
                                                {t(
                                                    'settings.emailSettings.configurationTypeHeader'
                                                )}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography
                                                variant="body2"
                                                fontWeight={600}
                                                color="text.secondary"
                                                textAlign="center"
                                            >
                                                {t(
                                                    'settings.emailSettings.emailAccountHeader'
                                                )}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <Divider />

                                    {/* Configuration Rows */}
                                    {localMailboxConfiguration.map(
                                        (
                                            config: IMailboxConfiguration,
                                            index
                                        ) => (
                                            <Box key={config.id}>
                                                <Grid
                                                    container
                                                    spacing={2}
                                                    alignItems="center"
                                                    sx={{ py: 2 }}
                                                >
                                                    <Grid item xs={6}>
                                                        <Box>
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight={600}
                                                            >
                                                                {getMailboxTypeName(
                                                                    config.mailboxType
                                                                )}
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                            >
                                                                {getMailboxTypeDescription(
                                                                    config.mailboxType
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                    <Grid
                                                        item
                                                        xs={6}
                                                        display="flex"
                                                        justifyContent="center"
                                                    >
                                                        <FormControl
                                                            fullWidth
                                                            size="small"
                                                        >
                                                            <Select
                                                                value={
                                                                    config.mailbox?.toString() ||
                                                                    ''
                                                                }
                                                                onChange={(e) =>
                                                                    handleMailboxConfigurationChange(
                                                                        config.id,
                                                                        e.target
                                                                            .value as string
                                                                    )
                                                                }
                                                                displayEmpty
                                                            >
                                                                <MenuItem value="">
                                                                    <em>
                                                                        {t(
                                                                            'settings.emailSettings.noEmailSelected'
                                                                        )}
                                                                    </em>
                                                                </MenuItem>
                                                                {mailboxes
                                                                    .filter(
                                                                        (mb) =>
                                                                            mb.status ===
                                                                            'ACTIVE'
                                                                    )
                                                                    .map(
                                                                        (
                                                                            mailbox
                                                                        ) => (
                                                                            <MenuItem
                                                                                key={
                                                                                    mailbox.id
                                                                                }
                                                                                value={mailbox.id.toString()}
                                                                            >
                                                                                <Stack
                                                                                    direction="row"
                                                                                    spacing={
                                                                                        1
                                                                                    }
                                                                                    alignItems="center"
                                                                                >
                                                                                    <Icon
                                                                                        name={
                                                                                            mailbox.provider ===
                                                                                            MailProviders.GMAIL
                                                                                                ? 'GoogleLogoIcon'
                                                                                                : 'MicrosoftLogoIcon'
                                                                                        }
                                                                                        size={
                                                                                            18
                                                                                        }
                                                                                    />
                                                                                    <span>
                                                                                        {
                                                                                            mailbox.emailAddress
                                                                                        }
                                                                                    </span>
                                                                                </Stack>
                                                                            </MenuItem>
                                                                        )
                                                                    )}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                </Grid>
                                                {index <
                                                    localMailboxConfiguration.length -
                                                        1 && <Divider />}
                                            </Box>
                                        )
                                    )}

                                    {/* Save Button */}
                                    {hasUnsavedChanges && (
                                        <Box
                                            sx={{
                                                mt: 3,
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                            }}
                                        >
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={handleSaveConfiguration}
                                                disabled={isUpdateLoading}
                                            >
                                                {isUpdateLoading
                                                    ? t(
                                                          'settings.emailSettings.saving'
                                                      )
                                                    : t(
                                                          'settings.emailSettings.saveConfiguration'
                                                      )}
                                            </Button>
                                        </Box>
                                    )}
                                </Grid>
                            </>
                        )}
                    </>
                )}

                {/* Show message when no emails connected */}
                {!hasEmails && (
                    <Grid item xs={12}>
                        <Alert
                            severity="info"
                            sx={{
                                '& .MuiAlert-message': {
                                    color: 'text.primary',
                                },
                            }}
                        >
                            {t('settings.emailSettings.noEmailsConnected')}
                        </Alert>
                    </Grid>
                )}
                {!isUserContext && (
                    <>
                        <Grid item xs={12}>
                            <Divider />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ mb: 1 }}
                            >
                                {t('settings.emailSettings.customizeTemplate')}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 2 }}
                            >
                                {t(
                                    'settings.emailSettings.customizeTemplateDescription'
                                )}
                            </Typography>
                            <Button
                                variant="outlined"
                                onClick={handleCustomizeTemplate}
                            >
                                {t('settings.emailSettings.templateButton')}
                            </Button>
                        </Grid>
                    </>
                )}
            </Grid>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmDialog.open}
                onClose={handleCancelDelete}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {t('settings.emailSettings.removeEmailConfirmation')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('settings.emailSettings.removeEmailMessage')}
                    </DialogContentText>
                    {deleteConfirmDialog.mailbox && (
                        <Box
                            sx={{
                                mt: 2,
                                p: 2,
                                bgcolor: 'grey.50',
                                borderRadius: 1,
                            }}
                        >
                            <Typography variant="body2" fontWeight={600}>
                                {deleteConfirmDialog.mailbox.emailAddress}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleCancelDelete} variant="outlined">
                        {t('settings.emailSettings.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                        autoFocus
                    >
                        {t('settings.emailSettings.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EmailSettingsTab;
